import type { ComponentType, SVGProps } from "react";
import {
  HomeIcon,
  PaperAirplaneIcon,
  InboxArrowDownIcon,
  ArrowsRightLeftIcon,
  UserGroupIcon,
  TicketIcon,
  UsersIcon,
  DevicePhoneMobileIcon,
} from "@heroicons/react/24/outline";

import { Route, routes } from "../constants";
import { canAccessRoute } from "../utils/authorization";
import { BusinessType } from "../services/queries/businesses/types";
import { UserRole } from "../services/queries/users/types";

export type NavIcon = ComponentType<SVGProps<SVGSVGElement>>;

export enum NavSection {
  OVERVIEW = "OVERVIEW",
  PAYMENTS = "PAYMENTS",
  TRAVEL = "TRAVEL",
  ORGANIZATION = "ORGANIZATION",
}

// null label = items render without a section heading (top of sidebar)
export const sectionLabels: Record<NavSection, string | null> = {
  [NavSection.OVERVIEW]: null,
  [NavSection.PAYMENTS]: "Payments",
  [NavSection.TRAVEL]: "Travel",
  [NavSection.ORGANIZATION]: "Organization",
};

const sectionOrder: NavSection[] = [
  NavSection.OVERVIEW,
  NavSection.PAYMENTS,
  NavSection.TRAVEL,
  NavSection.ORGANIZATION,
];

// Presentation metadata keyed by route slug. Access policy stays on the
// route entries in constants; this file only decides how nav renders.
const navMeta: Record<string, { icon: NavIcon; section: NavSection }> = {
  dashboard: { icon: HomeIcon, section: NavSection.OVERVIEW },
  send: { icon: PaperAirplaneIcon, section: NavSection.PAYMENTS },
  collections: { icon: InboxArrowDownIcon, section: NavSection.PAYMENTS },
  transactions: { icon: ArrowsRightLeftIcon, section: NavSection.PAYMENTS },
  passengers: { icon: UserGroupIcon, section: NavSection.TRAVEL },
  flights: { icon: TicketIcon, section: NavSection.TRAVEL },
  esims: { icon: DevicePhoneMobileIcon, section: NavSection.TRAVEL },
  users: { icon: UsersIcon, section: NavSection.ORGANIZATION },
};

export interface NavItemConfig {
  path: string;
  title: string;
  slug: string;
  icon: NavIcon;
}

export interface NavSectionConfig {
  section: NavSection;
  label: string | null;
  items: NavItemConfig[];
}

/**
 * Builds the sidebar sections for the current session. Sections with no
 * accessible items are dropped entirely (no empty headings).
 */
export function buildNavSections(
  businessType?: BusinessType,
  userRole?: UserRole,
): NavSectionConfig[] {
  // Session must be loaded before any nav renders (matches previous
  // behavior); canAccessRoute alone is fail-open on missing context
  // because route guards need that leniency during the post-login race.
  if (!businessType || !userRole) return [];

  const visible = routes.filter(
    (route: Route) =>
      route.isAppRoute &&
      route.showInSideNav &&
      navMeta[route.slug] &&
      canAccessRoute(route, businessType, userRole),
  );

  return sectionOrder
    .map((section) => ({
      section,
      label: sectionLabels[section],
      items: visible
        .filter((route) => navMeta[route.slug]?.section === section)
        .map((route) => {
          const meta = navMeta[route.slug] as {
            icon: NavIcon;
            section: NavSection;
          };
          return {
            path: route.path,
            title: route.title,
            slug: route.slug,
            icon: meta.icon,
          };
        }),
    }))
    .filter((group) => group.items.length > 0);
}
